import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { appendCriteriaEquals, readPagedRows } from '@/shared/utils/spring-api';
import { districtDefaultValue } from '../model/district.model';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
function normalizeDistrictQuery(params) {
    if (params == null)
        return {};
    if (typeof params === 'string' || typeof params === 'number') {
        return { provinceId: params };
    }
    return params;
}
function buildDistrictsUrl(params) {
    const { provinceId, page, size, sort, filter } = normalizeDistrictQuery(params);
    const q = new URLSearchParams();
    if (page != null || size != null || sort || provinceId != null) {
        q.set('page', String(page ?? 0));
        q.set('size', String(size ?? 1000));
        if (sort)
            q.set('sort', sort);
    }
    if (filter)
        q.set('filter', filter);
    appendCriteriaEquals(q, 'provinceId', provinceId);
    const qs = q.toString();
    return qs ? `${apiUrl}?${qs}` : apiUrl;
}
const apiUrl = '/api/districts';
const DISTRICT_ALREADY_EXISTS_MESSAGE = 'District already exists.';
function isDistrictConcurrencyFailure(error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 409)
        return false;
    const data = error.response.data;
    if (!data || typeof data !== 'object')
        return false;
    return data.message === 'error.concurrencyFailure';
}
const initialState = {
    loading: false,
    errorMessage: null,
    districts: [],
    district: districtDefaultValue,
    updating: false,
    updateSuccess: false,
    totalItems: 0,
};
export const getDistricts = createAsyncThunk('districtManagement/fetch_districts', async (params) => axios.get(buildDistrictsUrl(params)), { serializeError: serializeAxiosError });
export const getDistrict = createAsyncThunk('districtManagement/fetch_district', async (id) => axios.get(`${apiUrl}/${id}`), { serializeError: serializeAxiosError });
export const getDistrictCount = createAsyncThunk('districtManagement/fetch_district_count', async (provinceId) => {
    const q = new URLSearchParams();
    appendCriteriaEquals(q, 'provinceId', provinceId);
    const qs = q.toString();
    return axios.get(qs ? `${apiUrl}/count?${qs}` : `${apiUrl}/count`);
}, { serializeError: serializeAxiosError });
export const createDistrict = createAsyncThunk('districtManagement/create_district', async (entity) => {
    try {
        return await axios.post(apiUrl, entity);
    }
    catch (error) {
        if (isDistrictConcurrencyFailure(error)) {
            throw new Error(DISTRICT_ALREADY_EXISTS_MESSAGE);
        }
        throw error;
    }
}, { serializeError: serializeAxiosError });
export const updateDistrict = createAsyncThunk('districtManagement/update_district', async (entity) => {
    const result = await axios.put(`${apiUrl}/${entity.id}`, entity);
    return result;
}, { serializeError: serializeAxiosError });
export const patchDistrict = createAsyncThunk('districtManagement/patch_district', async (entity) => {
    const { id, ...partial } = entity;
    const result = await axios.patch(`${apiUrl}/${id}`, partial);
    return result;
}, { serializeError: serializeAxiosError });
export const deleteDistrict = createAsyncThunk('districtManagement/delete_district', async (id) => axios.delete(`${apiUrl}/${id}`), { serializeError: serializeAxiosError });
export const DistrictManagementSlice = createSlice({
    name: 'districtManagement',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getDistrict.fulfilled, (state, action) => {
            state.loading = false;
            state.district = action.payload.data;
        })
            .addCase(getDistrictCount.fulfilled, (state, action) => {
            state.totalItems = action.payload.data;
        })
            .addCase(deleteDistrict.fulfilled, (state) => {
            state.updating = false;
            state.updateSuccess = true;
            state.district = districtDefaultValue;
        })
            .addMatcher(isFulfilled(getDistricts), (state, action) => {
            state.loading = false;
            const { rows, total } = readPagedRows(action.payload.data, action.payload.headers);
            state.districts = rows;
            state.totalItems = total;
        })
            .addMatcher(isFulfilled(createDistrict, updateDistrict, patchDistrict), (state, action) => {
            state.updating = false;
            state.updateSuccess = true;
            state.district = action.payload.data;
        })
            .addMatcher(isPending(getDistricts, getDistrict), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.loading = true;
        })
            .addMatcher(isPending(createDistrict, updateDistrict, patchDistrict, deleteDistrict), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.updating = true;
        })
            .addMatcher(isRejected(getDistricts, getDistrict, getDistrictCount, createDistrict, updateDistrict, patchDistrict, deleteDistrict), (state, action) => {
            state.loading = false;
            state.updating = false;
            state.updateSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset } = DistrictManagementSlice.actions;
export default DistrictManagementSlice.reducer;
